import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal } from "bootstrap";
import Profile from "../../../Assets/images/avatar.png";
import ProfileAvatar from '../../../Assets/images/avatar.png';
import Handler from "../../../Helpers/Handler";
import { useAuth } from "../../../Contexts/AuthenticateProvider";
import { formatDateTime } from "../../../Helpers/Utils";
import BackButton from "../../../Components/BackButton";
import AssociateVerification from "./AssociateVerification";
import ListLayout from "../../../Components/List";
import Timeline from "../../../Components/Timeline";
import { Badge, DetailCard, KycCard, Skeleton, Stat } from "../../../Components/Needed";
import { BsFillFuelPumpDieselFill, BsFillPatchCheckFill, BsFillPatchExclamationFill } from "react-icons/bs";
import { FaAddressCard, FaExclamation, FaIdCardAlt, FaPhoneAlt } from "react-icons/fa";
import { IoIosFemale, IoIosMail, IoIosMale } from "react-icons/io";
import { FaCircleCheck } from "react-icons/fa6";
import { MdAddTask, MdAppRegistration, MdCancel } from "react-icons/md";
import { CgProfile } from "react-icons/cg";
import { IoLocation, IoShieldCheckmarkOutline } from "react-icons/io5";
import { LuCalendarDays, LuMailCheck, LuMailWarning } from "react-icons/lu";
import { FiEdit } from "react-icons/fi";
import { GiCheckMark } from "react-icons/gi";
import { TbRosetteDiscountCheckFilled } from "react-icons/tb";
import { listBackPath } from "../../../Helpers/useListParams";

// Ported from the uploaded reference View.js - profile card, verification
// progress bar, KYC tiles, Aadhaar/PAN detail cards, associated cars list and
// activity timeline all kept as designed. Adapted to real endpoints:
//   - GET /staff/client/associate/details/:Id for the profile (verificationStage,
//     kyc.aadhaar/kyc.pan shape match the uploaded design 1:1 - see
//     staff_backend Controller/Client/ClientController.js associateData).
//   - Associated cars now come from their own call, GET
//     /staff/client/associate/car/details/:Id, rather than the details
//     response's embedded carDetails.
//   - GET /staff/client/associate/log/:Id?limit=N for the activity timeline.
//   - Status toggle uses PATCH .../status/:Id?status=true|false with a reason
//     required only when deactivating (matches ClientValidation.associateStatus).
//   - "Verify" opens the AssociateVerification modal's stages as designed;
//     final manual verify calls PATCH .../verify/:Id with no body (the
//     endpoint just flips verified=true once every KYC stage is complete).
const AssociateDetail = () => {
    const { hasPermission } = useAuth();
    const canEdit = hasPermission("associate.edit");
    const canSetStatus = hasPermission("associate.status");
    const canVerify = hasPermission("associate.verify");
    const canSeeLog = hasPermission("associate.timeline");
    // The car list below is a car-fleet view/action, not an associate one - it needs
    // vehicle.car.view/create the same way the standalone /car and /car/register
    // routes do (see Routers/index.js), regardless of whether the viewer can edit or
    // verify this associate.
    const canViewCars = hasPermission("vehicle.car.view");
    const canCreateCars = hasPermission("vehicle.car.create") && hasPermission("vehicle.brand.view") && hasPermission("vehicle.model.view");
    const { associateId } = useParams();
    const navigate = useNavigate();

    const [associateDetails, setAssociateDetails] = useState({});
    const [carDetails, setCarDetails] = useState([]);
    const [userLog, setUserLog] = useState([]);
    const [stage, setStage] = useState("");
    const [loading, setLoading] = useState(true);
    const [statusModal, setStatusModal] = useState(false);
    const [statusUpdating, setStatusUpdating] = useState(false);

    const verificationStage = associateDetails?.verificationStage || 0;
    const aadhaar = associateDetails?.kyc?.aadhaar;
    const pan = associateDetails?.kyc?.pan?.panData;

    const fetchAssociateDetails = useCallback(async () => {
        setLoading(true);
        const response = await Handler({ method: "get", url: `/staff/client/associate/details/${associateId}` });
        if (response?.success) {
            setAssociateDetails(response?.data?.data || {});
        } else {
            setAssociateDetails(null);
            toast.error(response?.data?.message || "Associate not found");
        }
        setLoading(false);
    }, [associateId]);

    const fetchCarDetails = useCallback(async () => {
        // Defense in depth, matching fetchUserLog below - the section that renders
        // this data is already hidden without vehicle.car.view, but skip the
        // network call entirely too rather than fetching data the viewer can't see.
        if (!canViewCars) return;
        const response = await Handler({ method: "get", url: `/staff/client/associate/car/details/${associateId}` });
        setCarDetails(response?.success ? response?.data?.data || [] : []);
    }, [associateId, canViewCars]);

    const fetchUserLog = useCallback(async () => {
        if (!canSeeLog) return;
        const response = await Handler({ method: "get", url: `/staff/client/associate/log/${associateId}`, params: { limit: 20 } });
        setUserLog(response?.success ? response?.data?.data?.data || [] : []);
    }, [associateId, canSeeLog]);

    useEffect(() => {
        fetchAssociateDetails();
        fetchCarDetails();
        fetchUserLog();
    }, [fetchAssociateDetails, fetchCarDetails, fetchUserLog]);

    const statusUpdate = async () => {
        if (associateDetails.status && !associateDetails.reason) {
            toast.error("Reason is required");
            return;
        }
        setStatusUpdating(true);
        const data = associateDetails.status ? { reason: associateDetails.reason || "" } : {};
        const response = await Handler({ method: "patch", url: `/staff/client/associate/status/${associateId}`, params: { status: !associateDetails.status }, data });
        if (response?.success) {
            toast.success(response?.data?.message || "Status updated");
            fetchAssociateDetails();
            setStatusModal(false);
        } else {
            toast.error(response?.data?.message || "Unable to update status");
        }
        setStatusUpdating(false);
    };
    const openStageModal = () => {
        const modalEl = document.getElementById("stageModal");
        if (modalEl) Modal.getOrCreateInstance(modalEl).show();
    };

    const changeVerificationStage = (nextStage) => {
        if (!canEdit) {
            toast.error("You don't have permission to edit this associate");
            return;
        }
        if (nextStage === "email") {
            // Email can be changed any number of times up until the associate itself
            // is fully verified - it isn't locked just because the current email
            // happens to already be verified. Once overall verification is done,
            // it's locked like everything else. Re-sending an OTP for the exact same
            // already-verified email is still rejected server-side (emailChangeOtp).
            if (!associateDetails?.verified) { setStage("email"); openStageModal(); } else { toast.error("This associate is already verified - email can no longer be changed"); }
        } else if (nextStage === "aadhaar") {
            if (!associateDetails?.kyc?.aadhaar?.aadhaarDetails) { setStage("aadhaar"); openStageModal(); } else { toast.error("Aadhaar is already verified"); }
        } else if (nextStage === "pan") {
            if (!associateDetails?.kyc?.pan?.panNo) { setStage("pan"); openStageModal(); } else { toast.error("PAN is already verified"); }
        }
    };

    const nextPendingStage = () => {
        if (!associateDetails?.photo) return "profile";
        if (!associateDetails?.email?.status) return "email";
        if (!associateDetails?.kyc?.aadhaar?.aadhaarDetails) return "aadhaar";
        if (!associateDetails?.kyc?.pan?.panNo) return "pan";
        return "";
    };

    const openVerifyFlow = () => {
        if (!canEdit) {
            toast.error("You don't have permission to edit this associate");
            return;
        }

        setStage(nextPendingStage());
    };

    const openManualVerify = () => {
        if (!canVerify) {
            toast.error("You don't have permission to verify this associate");
            return;
        }
        setStage("final");
    };

    const tableLayout = useMemo(() => [
        {
            key: "car",
            name: "Car",
            mandatory: true,
            render: (row) => (
                <div className="d-flex align-items-center gap-3">
                    <img src={row?.carImages?.length ? row.carImages[0] : ProfileAvatar} alt={row?.brand} className="rounded-circle border" width={40} height={40} style={{ objectFit: 'cover' }} />
                    <div className="d-flex flex-column">
                        <span className="fw-semibold text-dark text-capitalize">{(row?.brand || "") + " " + (row?.variantName || "")}</span>
                        <small className={`d-flex align-items-center gap-1 ${row?.verified ? "text-success" : "text-muted"}`}>
                            {row?.verified ? <i className="bi bi-patch-check-fill" /> : <i className="bi bi-x-circle" />}
                            {row?.verified ? "Verified" : "Unverified"}
                        </small>
                    </div>
                </div>
            ),
        },
        {
            key: "registrationNo",
            name: "Reg No",
            mandatory: true,
            render: (row) => (
                <div className="small">
                    <div className="text-dark">{row?.registrationNo || ""}</div>
                    <div className="text-dark">{row?.segment || ""}</div>
                </div>
            ),
        },
        {
            key: "fuelType",
            name: "Type",
            render: (row) => (
                <div className="small">
                    <div className="text-dark">{row?.fuelType ? (Array.isArray(row?.fuelType) ? row?.fuelType?.join(", ") : row?.fuelType) : ""}</div>
                    <div className="text-dark">{row?.transmissionType || ""}</div>
                </div>
            ),
        },
        { key: "createdAt", name: "Register Date", render: (row) => <span className="text-muted small">{row?.createdAt ? formatDateTime(row.createdAt) : ""}</span> },
        {
            key: "status",
            name: "Status",
            render: (row) => (
                <span className={`badge rounded-pill ${row?.status ? 'text-bg-success' : 'text-bg-danger'}`}>
                    <i className={`bi ${row?.status ? 'bi-check-circle' : 'bi-x-circle'}`}></i> {row?.status ? "Active" : "Inactive"}
                </span>
            ),
        },
    ], []);

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="30%" className="mb-3" />
                <Skeleton height={220} />
            </div>
        );
    }

    if (!associateDetails) {
        return (
            <div className="settings-card text-center py-5">
                <p className="text-muted mb-3">Associate not found.</p>
                <button type="button" className="btn back-btn" onClick={() => navigate(listBackPath("/associates"))}>Back to associates</button>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="container-fluid py-0">
                <div className="row">
                    <div className="col-12 mb-3">
                        <BackButton name="to List" url="/associates" />
                    </div>
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="p-3">
                                <div className="d-sm-flex justify-content-between mb-2">
                                    <h5 className="fw-semibold d-flex align-items-center justify-content-sm-strat justify-content-center mb-sm-0 mb-2 gap-2">
                                        <CgProfile /> Profile Details
                                    </h5>
                                    <div className="justify-content-sm-end d-flex align-items-center justify-content-center gap-1">
                                        {canEdit ? (
                                            <button className="btn d-flex align-items-center gap-1" onClick={() => navigate(`/associates/${associateId}/edit`)}>
                                                <FiEdit /><span> Edit</span>
                                            </button>
                                        ) : null}
                                        {canSetStatus ? (
                                            <button disabled={statusUpdating} className={`btn btn-sm ${associateDetails?.status ? 'btn-danger' : 'btn-success'}`} onClick={() => { setAssociateDetails((prev) => ({ ...prev, reason: "" })); setStatusModal(true); }}>
                                                {associateDetails?.status ? 'Deactivate' : 'Activate'}
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                                <hr className="m-0" />
                            </div>

                            <div className="card-body">
                                <div className="row">
                                    <div className="col-12 col-lg-7">
                                        <div className="d-flex flex-column flex-md-row gap-4">
                                            <img src={associateDetails?.photo || Profile} alt="profile" className="customer-profile-img" />
                                            <div>
                                                <h3 className="fw-bold text-capitalize mb-1">
                                                    {associateDetails?.firstName || "Guest"} {associateDetails?.lastName}
                                                </h3>
                                                <small className="text-muted d-block mb-3">#{associateDetails?.associateId}</small>
                                                <div className="mb-2 d-flex align-items-center">
                                                    <FaPhoneAlt className="me-2 fs-5 text-muted" /> +91 {associateDetails?.phone}
                                                </div>
                                                <div className="mb-2 d-flex align-items-center">
                                                    <IoIosMail className="me-2 fs-5 text-muted" />
                                                    {associateDetails?.email?.emailId ? <span className={associateDetails?.email?.status ? "" : "text-danger"}>{associateDetails?.email?.emailId}</span> : "Not Mentioned"}
                                                    {canEdit && !associateDetails?.verified ? (
                                                        <button className="btn py-0 px-1 ms-3 btn-link cursor-pointer" onClick={() => changeVerificationStage("email")}><FiEdit /></button>
                                                    ) : null}
                                                </div>
                                                <div className="mb-3 d-flex align-items-center">
                                                    <MdAddTask className="me-2 fs-5 text-muted" />
                                                    {associateDetails?.createdBy?.type} - {associateDetails?.createdBy?.name}
                                                </div>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {associateDetails?.gender ? (
                                                        <Badge
                                                            text={associateDetails?.gender === "male" ? "Male" : "Female"}
                                                            icon={associateDetails?.gender === "male" ? <IoIosMale /> : <IoIosFemale />}
                                                            className={associateDetails?.gender === "male" ? "badge-male" : "badge-female"}
                                                        />
                                                    ) : ""}
                                                    <Badge
                                                        text={associateDetails?.status ? "Active" : "Inactive"}
                                                        icon={associateDetails?.status ? <FaCircleCheck /> : <MdCancel />}
                                                        className={associateDetails?.status ? "badge-active" : "badge-inactive"}
                                                    />
                                                    <Badge
                                                        text={associateDetails?.verified ? "Verified" : "Not Verified"}
                                                        icon={associateDetails?.verified ? <BsFillPatchCheckFill /> : <BsFillPatchExclamationFill />}
                                                        className={associateDetails?.verified ? "badge-verified" : "badge-warning"}
                                                    />
                                                </div>
                                                {!associateDetails?.status && associateDetails?.errorMsg ? <div className="mt-2 text-danger">Reason: {associateDetails?.errorMsg}</div> : ""}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12 col-lg-5 mt-4 mt-lg-0">
                                        <div className="stats-wrapper p-4 rounded-4">
                                            <div className="row text-center align-items-center justify-content-center">
                                                <Stat label="Cars" value={associateDetails?.cars} />
                                                <Stat label="Bikes" value={associateDetails?.bikes} />
                                            </div>
                                            <hr />
                                            <div>
                                                {verificationStage !== 100 ? (
                                                    <>
                                                        <div className="d-flex justify-content-between">
                                                            <span className="small text-muted">Verification Progress</span>
                                                            <span>{verificationStage}%</span>
                                                        </div>
                                                        <div className="progress rounded-pill mt-2">
                                                            <div className="progress-bar bg-success" style={{ width: `${verificationStage}%` }} />
                                                        </div>
                                                    </>
                                                ) : null}
                                                <div className="mt-4 text-center">
                                                    {verificationStage !== 100 ? (
                                                        canEdit ? (
                                                            <button className="btn btn-primary btn-sm cursor-pointer" onClick={openVerifyFlow} data-bs-toggle="modal" data-bs-target="#stageModal">
                                                                <FaExclamation /> Verify
                                                            </button>
                                                        ) : (
                                                            <div className="alert alert-warning small mb-0 py-2">You don't have permission to edit this associate's verification.</div>
                                                        )
                                                    ) : associateDetails?.verified ? (
                                                        <p className="m-0 text-success fs-6 align-center"><GiCheckMark className="me-2 fs-5" /> Verification Completed</p>
                                                    ) : canVerify ? (
                                                        <button className="btn btn-primary btn-sm cursor-pointer" onClick={openManualVerify} data-bs-toggle="modal" data-bs-target="#stageModal">
                                                            <FaExclamation />Manual Verify
                                                        </button>
                                                    ) : (
                                                        <div className="alert alert-warning small mb-0 py-2">You don't have permission to verify this associate.</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-xl-8">
                        <div className="card border-0 shadow-sm rounded-4 mb-4">
                            <div className="p-3">
                                <h5 className="fw-semibold d-flex align-items-center gap-2"><IoShieldCheckmarkOutline /> KYC Status</h5>
                                <hr className="m-0" />
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <KycCard title="Email" onClick={() => { changeVerificationStage("email"); }} verified={associateDetails?.email?.status} icon={associateDetails?.email?.status ? <LuMailCheck /> : <LuMailWarning />} />
                                    <KycCard title="Aadhaar" onClick={() => changeVerificationStage("aadhaar")} verified={!!associateDetails?.kyc?.aadhaar?.aadhaarDetails} icon={<FaAddressCard />} />
                                    <KycCard title="Pan" onClick={() => changeVerificationStage("pan")} verified={!!associateDetails?.kyc?.pan?.panNo} icon={<FaIdCardAlt />} />
                                </div>
                            </div>
                        </div>
                        {aadhaar?.aadhaarDetails && (
                            <DetailCard title="Aadhaar Details" icon={<FaAddressCard />}>
                                <div className="aadhaar-hero mb-4 p-4">
                                    <div className="row align-items-center">
                                        {aadhaar?.aadhaarDetails?.aadhaar_xml_data?.profile_image && (
                                            <div className="col-md-3 text-center mb-3 mb-md-0">
                                                <img src={`data:image/jpeg;base64,${aadhaar?.aadhaarDetails?.aadhaar_xml_data?.profile_image}`} className="aadhaar-photo" alt="Aadhaar" />
                                            </div>
                                        )}
                                        <div className="col-md-9">
                                            <div className="info-grid">
                                                <div className="info-field"><div className="info-field-label">FULL NAME</div><div className="info-field-value">{aadhaar?.aadhaarDetails?.aadhaar_xml_data?.full_name || "-"}</div></div>
                                                <div className="info-field"><div className="info-field-label">GENDER</div><div className="info-field-value">{aadhaar?.aadhaarDetails?.aadhaar_xml_data?.gender === "M" ? "Male" : "Female"}</div></div>
                                                <div className="info-field"><div className="info-field-label">DATE OF BIRTH</div><div className="info-field-value">{aadhaar?.aadhaarDetails?.aadhaar_xml_data?.dob || "-"}</div></div>
                                                <div className="info-field"><div className="info-field-label">MOBILE NUMBER</div><div className="info-field-value">+91 {aadhaar?.aadhaarDetails?.digilocker_metadata?.mobile_number || "-"}</div></div>
                                                <div className="info-field"><div className="info-field-label">AADHAAR NUMBER</div><div className="info-field-value">{aadhaar?.aadhaarDetails?.aadhaar_xml_data?.masked_aadhaar || "-"}</div></div>
                                                <div className="info-field"><div className="info-field-label">YEAR OF BIRTH</div><div className="info-field-value">{aadhaar?.aadhaarDetails?.aadhaar_xml_data?.yob || "-"}</div></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-4 px-3">
                                    <h4 className="mb-3"><IoLocation className="text-primary" /> Address Details</h4>
                                    <div className="address-display mb-4">
                                        <h6>Full Address</h6>
                                        {aadhaar?.aadhaarDetails?.aadhaar_xml_data?.full_address ? (
                                            <p className="m-0">{aadhaar?.aadhaarDetails?.aadhaar_xml_data?.full_address}</p>
                                        ) : (
                                            <p className="m-0 text-muted">No address details available</p>
                                        )}
                                    </div>
                                    <div className="info-grid px-3">
                                        <div className="info-field"><div className="info-field-label">House Number</div><div className="info-value">{aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.house || "-"}</div></div>
                                        <div className="info-field"><div className="info-field-label">Street</div><div className="info-value">{aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.street || "-"}</div></div>
                                        <div className="info-field"><div className="info-field-label">Landmark</div><div className="info-value">{aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.landmark || "-"}</div></div>
                                        <div className="info-field"><div className="info-field-label">Locality</div><div className="info-value">{aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.loc || "-"}</div></div>
                                        <div className="info-field"><div className="info-field-label">Village/Town/City</div><div className="info-value">{aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.vtc || "-"}</div></div>
                                        <div className="info-field"><div className="info-field-label">Sub District</div><div className="info-value">{aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.subdist || "-"}</div></div>
                                        <div className="info-field"><div className="info-field-label">District</div><div className="info-value">{aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.dist || "-"}</div></div>
                                        <div className="info-field"><div className="info-field-label">State</div><div className="info-value">{aadhaar?.aadhaarDetails?.aadhaar_xml_data?.address?.state || "-"}</div></div>
                                        <div className="info-field"><div className="info-field-label">Pincode</div><div className="info-value">{aadhaar?.aadhaarDetails?.aadhaar_xml_data?.zip || "-"}</div></div>
                                    </div>
                                </div>
                            </DetailCard>
                        )}

                        {pan?.name && (
                            <DetailCard title="Pan Details" icon={<FaIdCardAlt />}>
                                <div className="voter-hero p-4 mb-4">
                                    <div className="row align-items-center">
                                        <div className="col-md-8">
                                            <h4 className="fw-bold mb-1">{pan?.name || "-"}</h4>
                                            <span className="text-muted text-white">{pan?.category || ""}</span>
                                        </div>
                                        <div className="col-md-4 text-md-end mt-3 mt-md-0">
                                            <span className={`badge fs-6 px-3 py-2 ${pan?.idStatus === "VALID" ? "bg-success" : "bg-danger"}`}>Status: {pan?.idStatus || "-"}</span>
                                        </div>
                                    </div>
                                </div>
                            </DetailCard>
                        )}
                        {associateDetails?.verified && canViewCars ?
                            <ListLayout
                                showPage={true}
                                title={"Car"}
                                loading={loading}
                                addList={canCreateCars ? () => navigate(`/car/register/${associateId}`) : undefined}
                                data={{ list: carDetails || [], previous: false, next: false }}
                                table={tableLayout}
                                filterItems={{}}
                                initialFilters={{}}
                                onRowClick={(item) => navigate(`/car/details/${item.carId}`)}
                                filters={{}}
                            />
                            : ""}
                    </div>
                    <div className="col-12 col-xl-4 mt-xl-0 mt-3">
                        {canSeeLog ? (
                            loading ? (
                                <div className="card p-4 rounded-4 shadow-sm">
                                    <Skeleton height={25} width="60%" className="mb-3" />
                                    <Skeleton height={15} className="mb-2" />
                                    <Skeleton height={15} className="mb-2" />
                                    <Skeleton height={15} className="mb-2" />
                                </div>
                            ) : (
                                <Timeline userLog={userLog} />
                            )
                        ) : null}
                    </div>
                </div>

                <AssociateVerification stage={stage} associateId={associateId} onVerified={fetchAssociateDetails} />
            </div>

            {statusModal && (
                <div className="modal fade show" style={{ display: "block" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirmation</h5>
                                <button className="btn-close" onClick={() => setStatusModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <label className="form-label mb-3">Do you really wanna {associateDetails?.status ? "Deactivate" : "Activate"} this associate?</label>
                                {associateDetails?.status ? (
                                    <input autoComplete="off" value={associateDetails.reason || ""} name="reason" onChange={(e) => setAssociateDetails((prev) => ({ ...prev, reason: e.target.value }))} type="text" className="form-control" placeholder="Reason" />
                                ) : ""}
                            </div>
                            <div className="modal-footer align-items-center justify-content-center">
                                <button disabled={statusUpdating} className="btn btn-success" onClick={statusUpdate}>Yes</button>
                                <button disabled={statusUpdating} className="btn btn-danger" onClick={() => setStatusModal(false)}>No</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {statusModal && <div className="modal-backdrop fade show"></div>}
        </React.Fragment>
    );
};

export default AssociateDetail;
